import { useState } from "react";
import type { Bootstrap, PaymentPlan, PlanInstalment } from "../../shared/types";
import { api } from "../lib/api";
import { formatMoney, formatDayMonthYear, formatPeriod } from "../lib/format";
import StatCard from "../components/StatCard";
import PlanModal from "../components/PlanModal";
import { useConfirm } from "../components/ConfirmProvider";

// Instalment plans (Klarna, Clearpay, PayPal Pay in 3, …). This month's
// instalment flows into This Month automatically.
export default function PaymentPlans({
  data,
  reload,
}: {
  data: Bootstrap | null;
  reload: () => Promise<void>;
}) {
  const [modal, setModal] = useState(false);
  const [editPlan, setEditPlan] = useState<PaymentPlan | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [archiveErr, setArchiveErr] = useState<string | null>(null);
  // Long schedules (e.g. 24 monthly payments) collapse by default so the page
  // stays scannable; short ones stay open. `open[id]` overrides per plan.
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const ask = useConfirm();

  if (!data) return <div className="state">Loading…</div>;

  const period = data.summary.period;
  const plans = data.plans;
  const dueThisMonth = plans.reduce(
    (t, p) => t + p.instalments.filter((i) => i.period === period).reduce((s, i) => s + i.amountMinor, 0), 0);
  const outstanding = plans.reduce((t, p) => t + p.outstandingMinor, 0);
  const endingThisMonth = plans.filter((p) => p.endPeriod === period).length;

  async function restore(id: number) {
    await api.restorePlan(id);
    await reload();
  }

  async function archive(p: PaymentPlan) {
    if (!(await ask({ title: "Archive plan?", message: `Archive "${p.name}"? It moves to Archived and stops counting, but stays in your history.`, confirmLabel: "Archive" }))) return;
    setArchiveErr(null);
    try {
      await api.deletePlan(p.id);
      await reload();
    } catch (e) {
      setArchiveErr((e as Error).message);
    }
  }

  async function toggle(plan: PaymentPlan, inst: PlanInstalment) {
    const key = `${plan.id}:${inst.seq}`;
    if (busy === key) return;
    setBusy(key);
    try {
      await api.tickPlan(plan.id, inst.seq, !inst.paid);
      await reload();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Payment plans</h1>
          <p className="page__lede">
            Klarna, Pay in 3 &amp; instalments. This month’s payments flow into This Month automatically. Plans move to Archived on their own once the final payment date passes — or archive one yourself once every payment is ticked off.
          </p>
        </div>
        <button className="btn btn--primary" onClick={() => { setEditPlan(null); setModal(true); }}>+ Add plan</button>
      </div>

      <section className="stat-grid">
        <StatCard label="Due this month" value={formatMoney(dueThisMonth)} sub="pulled into This Month" />
        <StatCard label="Outstanding balance" value={formatMoney(outstanding)} sub="Across all live plans" accent />
        <StatCard label="Active plans" value={String(plans.length)} sub={endingThisMonth ? `${endingThisMonth} ending this month` : "None ending this month"} />
      </section>

      {plans.length === 0 && (
        <div className="panel empty">No payment plans yet. Add Klarna, Clearpay or Pay-in-3 plans to track them.</div>
      )}

      {plans.map((p) => {
        const total = p.totalMinor;
        const paidPct = total > 0 ? Math.round(((total - p.outstandingMinor) / total) * 100) : 0;
        const first = p.instalments[0]?.dueDate;
        const last = p.instalments[p.instalments.length - 1]?.dueDate;
        return (
          <section className="plan-card" key={p.id}>
            <div className="plan-card__head">
              <span className="avatar avatar--lg" style={{ background: p.paidByColour ?? "#98A2B3" }}>{p.paidByInitial ?? "?"}</span>
              <div className="plan-card__title">
                <div className="plan-card__name">
                  {p.name}
                  {p.provider && <span className="type-tag type-tag--plan">{p.provider}</span>}
                </div>
                <p className="plan-card__meta">
                  {formatMoney(p.amountMinor)} × {p.numPayments} payments · Monthly
                  {first && last && <> · {formatDayMonthYear(first)} → {formatDayMonthYear(last)}</>}
                </p>
              </div>
              <div className="bill-table__icons">
                <button className="icon-btn" onClick={() => { setEditPlan(p); setModal(true); }} aria-label="Edit plan" title="Edit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
                </button>
              </div>
            </div>

            <div className="plan-card__progress-row">
              <span>{p.paidCount} of {p.numPayments} paid · {formatMoney(total)} total</span>
              <strong>{formatMoney(p.outstandingMinor)} left</strong>
            </div>
            <div className="meter"><span style={{ width: `${paidPct}%`, background: "var(--color-primary)" }} /></div>

            {(() => {
              const collapsible = p.numPayments > 8;
              const isOpen = collapsible ? (open[p.id] ?? false) : true;
              const nextDue = p.instalments.find((i) => !i.paid);
              const shown = isOpen ? p.instalments : nextDue ? [nextDue] : [];
              return (
                <>
                  {!isOpen && nextDue && <p className="plan-card__next">Next due</p>}
                  <div className="plan-schedule">
                    {shown.map((inst) => (
                      <label className={"plan-inst" + (inst.period === period ? " is-current" : "")} key={inst.seq}>
                        <input type="checkbox" className="bill-row__check" checked={inst.paid} disabled={busy === `${p.id}:${inst.seq}`} onChange={() => toggle(p, inst)} />
                        <div>
                          <span className="plan-inst__date">{formatDayMonthYear(inst.dueDate)}</span>
                          <span className="plan-inst__amt">{formatMoney(inst.amountMinor)}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  {collapsible && (
                    <button
                      type="button"
                      className="plan-card__toggle"
                      onClick={() => setOpen((o) => ({ ...o, [p.id]: !isOpen }))}
                    >
                      {isOpen ? "Show less" : `Show all ${p.numPayments} payments`}
                    </button>
                  )}
                </>
              );
            })()}

            {p.paidCount === p.numPayments && (
              <button type="button" className="btn btn--sm plan-card__archive" onClick={() => archive(p)}>
                ✓ All paid — archive this plan
              </button>
            )}
          </section>
        );
      })}

      {archiveErr && <p className="form__error">{archiveErr}</p>}

      {data.archivedPlans.length > 0 && (
        <section className="panel archived-plans">
          <h3 className="panel__title">Archived plans</h3>
          <p className="panel__sub">Ended plans, kept for your records. Restore one if it starts up again.</p>
          {data.archivedPlans.map((p) => (
            <div className="archived-row" key={p.id}>
              <span className="avatar" style={{ background: p.paidByColour ?? "#98A2B3" }}>{p.paidByInitial ?? "?"}</span>
              <div className="archived-row__main">
                <span className="archived-row__name">
                  {p.name}
                  {p.provider && <span className="type-tag type-tag--plan">{p.provider}</span>}
                </span>
                <span className="archived-row__meta">
                  {formatMoney(p.totalMinor)} · {p.numPayments} payments · ended {formatPeriod(p.endPeriod)}
                </span>
              </div>
              <button className="btn btn--ghost btn--sm" onClick={() => restore(p.id)}>Restore</button>
            </div>
          ))}
        </section>
      )}

      {modal && (
        <PlanModal data={data} plan={editPlan} onClose={() => setModal(false)} onSaved={async () => { setModal(false); await reload(); }} />
      )}
    </div>
  );
}
